/* eslint-disable no-undef */

import { useConfigurationStore } from '@/stores/configurationModule';
import { useConfiguratorStore } from '@/stores/configurator';
import { setControlsSize } from '@/utils/common';

/**
 * Events:
 *  - zoom-in
 *  - zoom-out
 *  - reset-zoom
 */

export default function useZoomEventListeners() {
  const $eventBus = inject('bus');
  const canvasContainer = document.querySelector('.canvas-container');
  const configurationStore = useConfigurationStore();
  const {
    activeCanvas,
    box,
    hasInsidePrinting
  } = storeToRefs(configurationStore);
  const configuratorStore = useConfiguratorStore();
  const {
    setIsPreviewFullscreen,
    setIsEditorFullscreen
  } = configuratorStore;
  const {
    minVisibleFraction
  } = storeToRefs(configuratorStore);
  const {
    isEditorFullscreen,
    isPreviewFullscreen,
    zoomInEnabled,
    zoomOutEnabled,
    canvasContainerScale
  } = storeToRefs(configuratorStore);

  // Zoom
  const ZOOM_MIN = 0.7;
  const ZOOM_MAX = 2.2;
  const ZOOM_INCREMENT = 0.1;
  const INITIAL_ZOOM = hasInsidePrinting.value ? 0.9 : 1;
  let currentZoom = INITIAL_ZOOM; // @TODO: this has no effect on initial zoom level

  function calculateContainerX() {
    const startCanvasPos = canvasContainer.offsetLeft;
    const containerSize = canvasContainer.offsetWidth;
    const workspaceSize = workspace.offsetWidth;
    const scaledSize = containerSize * canvasContainerScale.value;
    const visible = workspaceSize * minVisibleFraction.value;
    let position = startCanvasPos;
    const offset = (containerSize - scaledSize) / 2;
    if (position > workspaceSize - offset - visible) {
      position = workspaceSize - offset - visible;
    }
    if (position < -(scaledSize + offset) + visible) {
      position = -(scaledSize + offset) + visible;
    }
    return position;
  }
  function calculateContainerY() {
    const startCanvasPos = canvasContainer.offsetTop;
    const containerSize = canvasContainer.offsetHeight;
    const workspaceSize = workspace.offsetHeight;
    const scaledSize = containerSize * canvasContainerScale.value;
    const visible = workspaceSize * minVisibleFraction.value;
    let position = startCanvasPos;
    const offset = (containerSize - scaledSize) / 2;
    if (position > workspaceSize - offset - visible) {
      position = workspaceSize - offset - visible;
    }
    if (position < -(scaledSize + offset) + visible) {
      position = -(scaledSize + offset) + visible;
    }
    return position;
  }
  function setCanvasContainerPosition() {
    const canvasContainerLeft = calculateContainerX();
    const canvasContainerTop = calculateContainerY();
    window.requestAnimationFrame(() => {
      canvasContainer.style.left = canvasContainerLeft + 'px';
      canvasContainer.style.top = canvasContainerTop + 'px';
    });
  }
  function applyZoom(change) {
    // Update currentZoom with boundary checks
    currentZoom = Math.min(Math.max(currentZoom + change, ZOOM_MIN), ZOOM_MAX);

    // Apply the scale transformation
    canvasContainerScale.value = currentZoom;

    // Update controls and re-render the canvas
    setControlsSize(box.value, canvasContainer.offsetWidth, currentZoom);
    // @NOTE: Why is this necessary?
    activeCanvas.value?.renderAll();
    setCanvasContainerPosition();

    // Enable/disable buttons based on current zoom level
    // @TODO: Move button state to component
    zoomInEnabled.value = currentZoom < ZOOM_MAX;
    zoomOutEnabled.value = currentZoom > ZOOM_MIN;
  }
  function resetZoom() {
    // Apply the scale transformation
    canvasContainerScale.value = INITIAL_ZOOM;
    setControlsSize(box.value, canvasContainer.offsetWidth, 1);
    activeCanvas?.value?.renderAll();
    zoomInEnabled.value = true;
    zoomOutEnabled.value = true;
    currentZoom = INITIAL_ZOOM;
  }
  function handleZoomIn() {
    if (currentZoom < ZOOM_MAX) {
      applyZoom(ZOOM_INCREMENT);
    }
  }
  function handleZoomOut() {
    if (currentZoom > ZOOM_MIN) {
      applyZoom(-ZOOM_INCREMENT);
    }
  }
  $eventBus.$on('zoom-in', () => {
    handleZoomIn();
  });
  $eventBus.$on('zoom-out', () => {
    handleZoomOut();
  });
  $eventBus.$on('reset-zoom', () => {
    resetZoom();
  });

  // Fullscreen listeners
  $eventBus.$on('editor-fullscreen', () => {
    setIsPreviewFullscreen(false);
    setIsEditorFullscreen(!isEditorFullscreen.value);
  });
  $eventBus.$on('3d-fullscreen', () => {
    setIsEditorFullscreen(false);
    setIsPreviewFullscreen(!isPreviewFullscreen.value);
  });
}